import React, { useCallback, useMemo } from 'react'
import { formatTimestamp } from 'src/Utils/formatDate'
import { OwnerAssignmentButton } from 'src/components/Reusable/Buttons'
import { MODEL_TYPE } from '@trustero/trustero-api-web/lib/common/model_pb'
import { Timestamp } from 'google-protobuf/google/protobuf/timestamp_pb'
import { ChatContextSourceType } from '@trustero/trustero-api-web/lib/chatcontext/chatcontext_pb'
import {
  ChatContext,
  ChatContextSelection,
  useChatContextKnowledgeBases,
} from 'src/components/Reusable/ChatContext/useChatContext.hooks'
import { MultiSelectModal } from 'src/components/Reusable/MultiSelectModal'
import {
  ContextSelectorOptionalDataContainer,
  ContextSelectorTableText,
} from '../chatContextPanel.styles'

export type KbModalProps = {
  show: boolean
  hide: () => void
  onBack?: () => void
  context: ChatContext
  setContext: (context: ChatContext) => void
}

export const KbModal = ({
  show,
  hide,
  onBack,
  context,
  setContext,
}: KbModalProps): JSX.Element => {
  const currentSelection = useMemo(() => {
    return context[ChatContextSourceType.KNOWLEDGE_BASE]
  }, [context])

  const { data, isLoading, error } = useChatContextKnowledgeBases()

  const items = useMemo(() => {
    return (
      data?.getKnowledgeBasesList()?.map((kb) => ({
        id: kb.getId(),
        renderItems: [
          <ContextSelectorTableText key={`${kb.getId()}-name`}>
            {kb.getFileName()}
          </ContextSelectorTableText>,
          <ContextSelectorOptionalDataContainer key={`${kb.getId()}-selector`}>
            <OwnerAssignmentButton
              modelId={kb.getId()}
              modelType={MODEL_TYPE.QUESTIONNAIRE}
              email={kb.getUserEmail()}
              model={undefined}
              isDisabled
            />
            {kb.getCreatedAt() !== undefined
              ? formatTimestamp(kb.getCreatedAt() as Timestamp)
              : 'Unknown Date'}
          </ContextSelectorOptionalDataContainer>,
        ],
      })) ?? []
    )
  }, [data])

  const onCommit = useCallback(
    (newSelection: ChatContextSelection) => {
      setContext({
        ...context,
        [ChatContextSourceType.KNOWLEDGE_BASE]: newSelection,
      })
    },
    [context, setContext],
  )

  return (
    <MultiSelectModal
      title="Select Q&A Knowledge Bases"
      description="The AI agent will only consider the knowledge bases you select here during your conversation."
      itemType="knowledge base"
      currentSelection={currentSelection}
      onCommit={onCommit}
      show={show}
      hide={hide}
      onBack={onBack}
      isLoading={isLoading}
      items={items}
      error={error}
      numberOfColumns={2}
      gridTemplateColumnStyling="auto auto"
    />
  )
}
